import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { faChevronLeft, faChevronRight, faClose, IconDefinition, faHeart, faBars, faFileArrowDown } from '@fortawesome/free-solid-svg-icons';
import { ViewCourse, ViewCourseDefaultValues } from 'src/app/components/course/course';
import { AuthService } from 'src/app/services/auth.service';
import { CourseService } from 'src/app/services/course.service';
import { PdfViewerComponent } from 'src/app/components/participants/pdf-viewer/pdf-viewer.component';
import { firstValueFrom, Subscription } from 'rxjs';
import { ModalService } from 'src/app/services/modal.service';
import { ParticipantThemeService } from 'src/app/services/participant-theme.service';
import { TranslationService } from 'src/app/services/translation.service';

@Component({
    selector: 'app-open-course',
    templateUrl: './open-course.component.html',
    styleUrls: ['./open-course.component.scss'],
    standalone: false
})
export class OpenCourseComponent implements OnInit {
  @ViewChild(PdfViewerComponent) child: PdfViewerComponent | undefined;
  @Input() llttfPage = false;
  token: string = '';
  isParticipant: boolean = false;
  isInProgress: boolean = true;
  courseId: string = '';
  loggedInUserPublicId: string = '';
  course: ViewCourse = ViewCourseDefaultValues();
  ebookMaterials: any[] = [];
  courseMaterials: any[] = [];
  translations: Record<string, string> = {};
  otherCourseMaterials: any[] = [];
  /** Video / audio-slide materials shown in Course Contents (streamed in viewer). */
  streamCourseMaterials: any[] = [];
  materialId: string = '';
  closeIcon: IconDefinition = faClose;
  chevronRight: IconDefinition = faChevronRight;
  chevronLeft: IconDefinition = faChevronLeft;
  faBars = faBars;
  faDownload = faFileArrowDown;
  panel = { isOpen: true };
  modalSub!: Subscription;
  modalOpen = this.modalService.getModalState();
  modalContent = '';
  modalTitle = '';
  modalOkButtonValue = 'OK';
  modalCancelButtonValue = 'CANCEL';
  modalOkButtonText = 'Ok';
  modalCancelButtonText = 'Cancel';
  participantClassByRole: string = '';


  constructor (
    private router: Router,
    private route: ActivatedRoute,
    private authservice: AuthService,
    private courseservice: CourseService,
    private modalService: ModalService,
    private themeService: ParticipantThemeService,
    private translationService: TranslationService
  ) { }

  ngOnInit(): void {
    if (!this.authservice.checkTokenExpiry()) {
      this.router.navigateByUrl('/bb-login');
    }

    this.themeService.setParticipantTheme();
    this.participantClassByRole = this.themeService.participantClassByRole;
    this.llttfPage = this.themeService.llttfPage;

    this.isParticipant = this.authservice.getUserLoginData().isParticipant
    this.loggedInUserPublicId = this.authservice.getUserLoginData().publicId;

    this.route.params.subscribe({
      next:(params) => {
        console.log(params);
        this.courseId = params['id'];
        this.materialId = params['materialid'];
        this.getCourseDetails(this.courseId);
      }
    });

    this.translationService.getTranslation().subscribe({
      next: (t) => { this.translations = t; }
    });
  }

  /** Popup HTML for retired materials; server sends access-until (retirement + 4 months). */
  materialRetiredPopupHtml(material: { materialRetirementAccessUntil?: string }): string {
    const until = material.materialRetirementAccessUntil ?? '';
    const tmpl = this.translations['materialRetiredInfo'];
    if (!tmpl) {
      return `<div class="material-retired-info-pane"><p><strong>This material has been retired.</strong></p><p>You can keep using it until <strong>${until}</strong>. Be sure to download your workbooks and worksheets for this material before this date to save your progress.</p></div>`;
    }
    return tmpl.replace(/\{\{accessUntil\}\}/g, until);
  }

  getCourseDetails(courseid: string) {
    this.courseservice.getCourseDetailsForParticipant(courseid).subscribe({
      next:(response) => {
        this.course = response;
        this.courseMaterials = response.materialList;
        this.otherCourseMaterials = this.courseMaterials.filter(material => material.type === 'Worksheet');
        this.ebookMaterials = this.courseMaterials.filter(material => material.type === 'E-book');
        this.streamCourseMaterials = this.courseMaterials.filter(
          material => material.type === 'Video' || material.type === 'Audio/Slides');
        this.course.enrollmentStatus = response.participantCourseStatus;
        this.course.retired = response.retired;
        this.isInProgress = response.participantCourseStatus == "In Progress";
        this.course.favoriteFlag = response.isFavourite;
      }
    });
  }

  togglePanel() {
    this.panel.isOpen = !this.panel.isOpen;
  }

  handleFavouriteClick() {
    this.getCourseDetails(this.courseId);
  }


  public reOpenCourse() {
    this.courseservice.participantReOpenCourse(this.courseId).subscribe({
      next:(response) => {
        if(response) {
          this.getCourseDetails(this.courseId);
        }
      }
    });
  }

  public completeCourse() {
    this.courseservice.participantCompleteCourse(this.courseId).subscribe({
      next:(response) => {
        if(response) {
          this.getCourseDetails(this.courseId);
        }
      }
    });
  }

  public createPlan() {
    this.router.navigate(['/participants/plans/add-plan/']);
  }

  public async loadPDFFile(materialId: string): Promise<void> {
    if (materialId === this.materialId) {
      return;
    }

    try {
      if (this.child) {
        await firstValueFrom(this.child.saveCurrentPdfFormNow());
      }
    } catch (e) {
      console.error('Failed to save PDF form before switching material', e);
    }

    await this.router.navigate(['/participants/courses/open-course', this.courseId, materialId]);
  }
}
